import { BookOpen, CalendarDays, CalendarRange, Ellipsis, LayoutList, ListTodo, MapPin, Plus, Repeat, Rss, Settings, Sparkles, Sun } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useState, type ComponentType } from 'react';
import { QuickAdd, type AddTab } from './components/QuickAdd';
import { MarkPrompt, RedAlertBanner } from './components/RedAlert';
import { Button, Loading } from './components/ui';
import { store } from './lib/device';
import { authQuery, useFeatures, useLocationReporting, usePlanStream, useSettings } from './lib/hooks';
import { navigate, setParams, useRoute } from './lib/router';
import { Toasts } from './lib/toast';
import { AssistantPage } from './pages/AssistantPage';
import { AuthPage } from './pages/AuthPage';
import { CalendarsPage } from './pages/CalendarsPage';
import { EverythingPage } from './pages/EverythingPage';
import { PlacesPage } from './pages/PlacesPage';
import { RoutinesPage } from './pages/RoutinesPage';
import { SettingsPage } from './pages/SettingsPage';
import { TasksPage } from './pages/TasksPage';
import { TodayPage } from './pages/TodayPage';
import { WeekPage } from './pages/WeekPage';

// The vault viewer (markdown, maths, the graph) loads only when Notes is opened.
const NotesPage = lazy(() => import('./pages/NotesPage').then((m) => ({ default: m.NotesPage })));

interface NavItem {
  path: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
}

const NAV: NavItem[] = [
  { path: '/today', label: 'Today', icon: Sun },
  { path: '/week', label: 'Week', icon: CalendarRange },
  { path: '/tasks', label: 'Tasks', icon: ListTodo },
  { path: '/routines', label: 'Routines', icon: Repeat },
  { path: '/calendars', label: 'Calendars', icon: Rss },
  { path: '/places', label: 'Places', icon: MapPin },
  { path: '/settings', label: 'Settings', icon: Settings },
];
const MOBILE_NAV = NAV.slice(0, 4);

export function App() {
  const qc = useQueryClient();
  const auth = useQuery({ ...authQuery, retry: 1 });

  useEffect(() => {
    const on = () => qc.invalidateQueries({ queryKey: ['auth'] });
    window.addEventListener('tempo:unauthorized', on);
    return () => window.removeEventListener('tempo:unauthorized', on);
  }, [qc]);

  if (auth.isLoading) return <Loading />;
  if (auth.error)
    return (
      <div className="auth">
        <div className="card stack">
          <h2>Can't reach Tempo</h2>
          <p className="subtle small">{(auth.error as Error).message}</p>
          <Button onClick={() => auth.refetch()}>Try again</Button>
        </div>
      </div>
    );
  if (!auth.data?.authenticated) {
    return <AuthPage setUp={!!auth.data?.setUp} setupCodeRequired={auth.data?.setupCodeRequired} onDone={() => qc.invalidateQueries()} />;
  }
  return <Shell />;
}

const MODE_KEY = 'tempo.mode';

type Mode = 'calendar' | 'assistant' | 'everything' | 'notes';
const MODES: { mode: Mode; path: string; label: string; icon: ComponentType<{ size?: number }> }[] = [
  { mode: 'calendar', path: '/today', label: 'Calendar', icon: CalendarDays },
  { mode: 'assistant', path: '/assistant', label: 'Assistant', icon: Sparkles },
  { mode: 'everything', path: '/everything', label: 'Everything', icon: LayoutList },
  { mode: 'notes', path: '/notes', label: 'Notes', icon: BookOpen },
];
const modeOf = (path: string): Mode => (path === '/assistant' ? 'assistant' : path === '/everything' ? 'everything' : path === '/notes' ? 'notes' : 'calendar');

/** Calendar | Assistant | Everything | Notes – the top-level modes. */
function ModeSwitch({ mode }: { mode: Mode }) {
  const { schoolManager } = useFeatures();
  // Everything and Notes show School Manager's tracker and vault, which only your Tempo has.
  const modes = schoolManager ? MODES : MODES.filter((m) => m.mode !== 'everything' && m.mode !== 'notes');
  return (
    <div className="mode-switch" role="tablist">
      {modes.map((m) => (
        <a key={m.mode} href={`#${m.path}`} role="tab" aria-selected={mode === m.mode} className={mode === m.mode ? 'on' : ''}>
          <m.icon size={15} />
          <span>{m.label}</span>
        </a>
      ))}
    </div>
  );
}

function Shell() {
  const route = useRoute();
  const settings = useSettings();
  const [add, setAdd] = useState<{ tab: AddTab; date?: string } | null>(null);
  usePlanStream(true);
  useLocationReporting();
  const mode = modeOf(route.path);
  const isAssistant = mode === 'assistant';

  // Open in whichever mode you used last.
  useEffect(() => {
    const last = MODES.find((m) => m.mode === store.get<string>(MODE_KEY, 'calendar'));
    if (!window.location.hash && last && last.mode !== 'calendar') navigate(last.path);
  }, []);
  useEffect(() => store.set(MODE_KEY, mode), [mode]);

  // Deep links like #/today?add=shifts (used by the home-screen shortcut).
  useEffect(() => {
    const a = route.params.get('add');
    if (a && route.path !== '/tasks') {
      setAdd({ tab: (['event', 'task', 'shifts', 'blackboard'].includes(a) ? a : 'event') as AddTab });
      setParams({ add: null });
    }
  }, [route]);

  if (settings.isLoading) return <Loading />;
  if (isAssistant) {
    return (
      <>
        <AssistantPage route={route} />
        <Toasts />
      </>
    );
  }
  const current = NAV.find((n) => route.path.startsWith(n.path)) ?? (route.path === '/more' || mode === 'everything' || mode === 'notes' ? null : NAV[0]);
  const openAdd = (date?: string) => setAdd({ tab: route.path === '/tasks' ? 'task' : 'event', date });

  let page;
  switch (route.path) {
    case '/week':
      page = <WeekPage route={route} />;
      break;
    case '/tasks':
      page = <TasksPage route={route} />;
      break;
    case '/routines':
      page = <RoutinesPage route={route} />;
      break;
    case '/calendars':
      page = <CalendarsPage />;
      break;
    case '/places':
      page = <PlacesPage />;
      break;
    case '/settings':
      page = <SettingsPage />;
      break;
    case '/more':
      page = <MorePage />;
      break;
    case '/everything':
      page = <EverythingPage />;
      break;
    case '/notes':
      page = (
        <Suspense fallback={<Loading />}>
          <NotesPage route={route} />
        </Suspense>
      );
      break;
    default:
      page = <TodayPage route={route} onAdd={openAdd} />;
  }

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <img src="/icons/icon.svg" alt="" /> Tempo
        </div>
        <ModeSwitch mode={mode} />
        {NAV.map((n) => (
          <a key={n.path} href={`#${n.path}`} className={`nav-item ${current?.path === n.path ? 'active' : ''}`}>
            <n.icon size={18} /> {n.label}
          </a>
        ))}
        <div className="spacer" />
        <Button variant="primary" block onClick={() => openAdd(route.params.get('date') ?? undefined)}>
          <Plus size={16} /> Add
        </Button>
      </aside>
      <main className={`main${mode === 'notes' ? ' main-notes' : ''}`}>
        <header className="topbar">
          <ModeSwitch mode={mode} />
          <span className="title">{current?.label ?? (mode === 'everything' || mode === 'notes' ? '' : 'More')}</span>
          <Button variant="primary" icon size="sm" onClick={() => openAdd(route.params.get('date') ?? undefined)} aria-label="Add">
            <Plus size={18} />
          </Button>
        </header>
        <MarkPrompt />
        {mode === 'calendar' && <RedAlertBanner />}
        {page}
      </main>
      <nav className="bottomnav">
        {MOBILE_NAV.map((n) => (
          <button key={n.path} className={current?.path === n.path ? 'active' : ''} onClick={() => navigate(n.path)}>
            <n.icon size={22} />
            {n.label}
          </button>
        ))}
        <button className={route.path === '/more' || (current && !MOBILE_NAV.includes(current)) ? 'active' : ''} onClick={() => navigate('/more')}>
          <Ellipsis size={22} />
          More
        </button>
      </nav>
      {add && <QuickAdd initial={add.tab} date={add.date} onClose={() => setAdd(null)} />}
      <Toasts />
    </div>
  );
}

function MorePage() {
  return (
    <div className="page more-list">
      {NAV.slice(4).map((n) => (
        <a key={n.path} href={`#${n.path}`} className="nav-item">
          <n.icon size={20} /> {n.label}
        </a>
      ))}
      <a href="#/today?add=shifts" className="nav-item">
        <CalendarDays size={20} /> Paste shifts
      </a>
    </div>
  );
}
